import * as React from "react";
import styled, { css } from "styled-components";
import { useHotkeys } from "react-hotkeys-hook";
import { DebounceInput } from "react-debounce-input";
import * as _ from "lodash";
import { useMappedState } from "redux-react-hook";

import { Icon } from "src/library/components/Icon";
import { InputCss } from "src/library/components/Forms/Input";
import Grid from "src/library/components/Hedron";
import { Modal } from "src/library/components/Modal";
import LoadingSpinner from "src/library/components/LoadingSpinner/LoadingSpinner";
import { Text } from "src/library/components/Text/Text";
import { Icons } from "src/library/components/Icon/types";
import SupportDocumentModal from "src/modules/support/components/SupportDocumentModal";
import SupportCard from "src/modules/support/components/SupportCard";
import type { IStore } from "src/core/shared/store";
import {
  ISupport,
  useSupportApi
} from "src/modules/support/api/support.api";
import { gtmSearchEvent } from "src/library/utils/tagmanager-events/helpers/gtm-search-event";

/**
 * Provides a search box for filtering
 * support documents
 */
const SupportSearch: React.ReactFCWithChildren = () => {
  const { theme } = useMappedState(({ global }: IStore) => ({ theme: global.settings && global.settings.theme }));
  const inputRef = React.useRef<HTMLInputElement>(null);

  /// hot key for control + s to search
  useHotkeys("ctrl+s", () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  });
  const [ supportDocuments, setSupportDocuments ] = React.useState<ISupport[]>([]);
  const [ selectedDocument, setSelectedDocument ] = React.useState<ISupport | null>(null);
  const [ listSupportResponse, listSupportRequest ] = useSupportApi("LIST_SUPPORT_DOCUMENTS");

  React.useEffect(() => {
    listSupportRequest(null);
  }, []);

  React.useEffect(() => {
    if (listSupportResponse.data) {
      setSupportDocuments(listSupportResponse.data);
    }
  }, [ listSupportResponse.data ]);

  const onSearch = React.useCallback(
    (searchValue: string) => {
      if (listSupportResponse.data) {
        setSupportDocuments(
          _.filter(listSupportResponse.data, sd => {
            if (sd.title.toLowerCase().includes(searchValue)) {
              return true;
            }

            return false;
          })
        );
        gtmSearchEvent("help", searchValue);
      }
    },
    [ listSupportResponse.data ]
  );

  return (
    <SupportSectionWrapper>
      <Text as="h2">
        Search for help
      </Text>

      <Grid.Bounds>
        <SearchWrapper>
          <DebounceInput
            inputRef={inputRef}
            className="search-input"
            placeholder="Search... (ctrl+s)"
            debounceTimeout={400}
            minLength={2}
            onChange={(e: React.ChangeEvent<any>) => onSearch(e.target.value)}
          />

          <Icon
            icon={Icons.SEARCH}
            $fillColor="lightGrey"
          />
        </SearchWrapper>
      </Grid.Bounds>

      {listSupportResponse.pending && (
        <LoadingSpinner
          type="circular"
          position="standalone"
        />
      )}

      <CardsContainer direction="horizontal">
        {_.map(supportDocuments, supportDoc => (
          <SupportCard
            key={`support-doc-${supportDoc.id}`}
            supportDocument={supportDoc}
            logo={theme ? theme.customDarkLogo : undefined}
            onSelect={d => setSelectedDocument(d)}
          />
        ))}
      </CardsContainer>

      {!!selectedDocument && (
        <Modal
          small={false}
          closeIcon
          body=""
          backgroundAction={() => setSelectedDocument(null)}
          show
          content={<SupportDocumentModal supportDocument={selectedDocument} />}
          title={selectedDocument.title}
        />
      )}
    </SupportSectionWrapper>
  );
};

const CardsContainer = styled(Grid.Bounds)`
  flex-wrap: wrap;
`;

const SearchWrapper = styled(Grid.Box)`
  ${({ theme }) => css`
    margin-right: ${theme.spacing.margins.md};
    position: relative;

    &:hover {
      svg {
        fill: ${theme.colors.lightGrey};
      }
    }

    .search-input {
      ${InputCss};
      width: 400px;
      border: solid 2px ${theme.colors.lightestGrey};
      border-radius: ${theme.borderRadius};
      padding-left: 35px;
      transition: ${theme.transition.quickest};
      font-size: ${theme.fonts.sizes.p};

      &:hover,
      &:focus {
        border-color: ${theme.colors.primaryDark};
        background-color: ${theme.colors.white};
      }
    }

    svg {
      transition: ${theme.transition.quickest};
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 5px;
    }
  `}
`;

const SupportSectionWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    margin-bottom: ${theme.spacing.padding.lg};
    padding-bottom: ${theme.spacing.padding.lg};
    border-bottom: 1px solid ${theme.colors.lightGrey};
  `}
`;

export default SupportSearch;
